'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiList, {
  type ListTypeMap as MuiListTypeMap,
} from '@mui/material/List';
import { listItemClasses } from '@mui/material/ListItem';
import { listItemAvatarClasses } from '@mui/material/ListItemAvatar';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { listItemTextClasses } from '@mui/material/ListItemText';

import { styled } from '../styled/styled';

type ExcludedMuiProps = '';

type CustomProps = {
  component?: ElementType;
  gap?: GapSize;
};

type ListTypeMap<
  P = CustomProps,
  D extends ElementType = MuiListTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiListTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `List` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-list-migration--list-migration-guide Migration guide}
 */
export type ListProps<
  D extends ElementType = MuiListTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiListTypeMap<P, D>, D>;

type GapSize = 'lg' | 'md' | 'sm';

const GAPSIZES: Record<GapSize, number> = {
  sm: 4,
  md: 5,
  lg: 7,
};

/**
 * Override min-width and padding left property for adjustable gap
 * https://serko.atlassian.net/browse/ZDS-301
 */
const StyledList = styled(MuiList, {
  shouldForwardProp: (propName) => {
    return propName !== 'gap';
  },
})<ListProps>(({ gap = 'md', theme }) => {
  const gapWidth = theme.spacing(GAPSIZES[gap]);
  return {
    [`& > .${listItemClasses.root} > .${listItemIconClasses.root}, & > .${listItemClasses.root} > .${listItemAvatarClasses.root}`]:
      {
        minWidth: gapWidth,
      },
    [`& > .${listItemClasses.root} > .${listItemTextClasses.inset}`]: {
      paddingLeft: gapWidth,
    },
  };
});

/**
 * @deprecated Please use `List` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-list-migration--list-migration-guide Migration guide}
 */
export const ListComponent = (props: ListProps, ref: Ref<HTMLUListElement>) => {
  return <StyledList {...props} ref={ref} />;
};

ListComponent.displayName = 'List';

/**
 * @deprecated Please use `List` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-list-migration--list-migration-guide Migration guide}
 */
export const List = forwardRef(
  ListComponent
) as OverridableComponent<ListTypeMap>;
